import { AppButton, AppInput, AppTextarea, SelectionDropdown, Typography } from "@libs/zapi-ui/custom";
import { GlobeIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { validateFilesLimit } from "@/zapi-frontend/shared/lib/validateFilesLimit";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import {
  ACCEPTED_FILE_TYPES,
  languageOptions,
  MAX_FILES,
  MAX_TOTAL_SIZE_BYTES,
  ProjectErrors,
  TMinFile,
  TMinFileOrFile,
  TProjectDataWithoutRepository
} from "@/zapi-frontend/widgets/CreateProjectForm";
import { useProjectTemplates } from "@/zapi-frontend/widgets/CreateProjectForm/model/useProjectTemplates";
import { RepositoryFile } from "./RepositoryFile";
import { AnalysisDropdown } from "./AnalysisDropdown";

export type TEditProjectFormProps = {
  projectData: TProjectDataWithoutRepository;
  setProjectData: Dispatch<SetStateAction<TProjectDataWithoutRepository>>;
  errors: ProjectErrors;
  setErrors: (errors: ProjectErrors) => void;
  files: TMinFileOrFile[];
  setFiles: (files: TMinFileOrFile[]) => void;
  onFileAdd: (files: File[]) => Promise<void>;
  onFileRemove: (file: TMinFile) => Promise<void>;
  onWebLinkAdd?: (url: string) => Promise<void>;
  onSubmit?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isLabs?: boolean;
};

export function EditProjectForm(props: TEditProjectFormProps) {
  const {
    projectData,
    setProjectData,
    errors,
    setErrors,
    files,
    setFiles,
    onFileAdd,
    onFileRemove,
    onWebLinkAdd,
    onSubmit,
    onCancel,
    isLoading = false,
    isLabs = false
  } = props;

  const [webLinkDialogOpen, setWebLinkDialogOpen] = useState(false);
  const [webLinkUrl, setWebLinkUrl] = useState("");
  const [isAddingWebLink, setIsAddingWebLink] = useState(false);

  const { projectTemplates } = useProjectTemplates();

  const updateProjectData = (updates: Partial<TProjectDataWithoutRepository>) => {
    setProjectData((prev) => ({ ...prev, ...updates }));
  };

  const updateErrors = (updates: Partial<ProjectErrors>) => {
    setErrors({ ...errors, ...updates });
  };

  const addFiles = async (newFiles: File[]) => {
    setFiles([...files, ...newFiles]);
    try {
      await onFileAdd(newFiles);
    } catch (error) {
      setFiles(files.filter((f) => !newFiles.includes(f as File)));
      throw error;
    }
  };

  const removeFile = async (fileToRemove: TMinFileOrFile) => {
    if (!(fileToRemove instanceof File) && fileToRemove.id > 0) {
      try {
        await onFileRemove(fileToRemove);
      } catch (error) {
        toast.error("Failed to delete file");
        throw error;
      }
    }
    setFiles(files.filter((f) => f !== fileToRemove));
  };

  const isMaxFilesExceeded = files.length >= MAX_FILES;
  const isMaxTotalSizeExceeded = files.reduce((acc, file) => acc + file.size, 0) > MAX_TOTAL_SIZE_BYTES;

  const validateForm = () => {
    const newErrors: ProjectErrors = {};
    let isValid = true;

    if (!projectData.name.trim()) {
      newErrors.name = "Project name is required";
      isValid = false;
    }

    if (!projectData.language) {
      newErrors.language = "Language is required";
      isValid = false;
    }

    if (!projectData.projectTemplateSlug) {
      newErrors.projectTemplateSlug = "Analysis type is required";
      isValid = false;
    }

    if (!projectData.decisionContext?.trim()) {
      newErrors.decisionContext = "Decision context is required";
      isValid = false;
    }

    if (!isValid) {
      updateErrors(newErrors);
      toast.error("Please fill in all required fields");
    }

    return isValid;
  };

  const attachmentFileInputRef = useRef<HTMLInputElement>(null);

  const handleAddWebLink = async () => {
    if (!webLinkUrl.trim() || !onWebLinkAdd) return;

    let url = webLinkUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }

    try {
      new URL(url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsAddingWebLink(true);
    try {
      await onWebLinkAdd(url);
      setWebLinkDialogOpen(false);
      setWebLinkUrl("");
      toast.success("Web link added");
    } catch {
      toast.error("Failed to add web link");
    } finally {
      setIsAddingWebLink(false);
    }
  };

  useEffect(() => {
    const projectTemplateChapters =
      projectTemplates.find((s) => s.slug === projectData.projectTemplateSlug)?.projectTemplateChapters ?? null;
    if (projectTemplateChapters) {
      if (!projectData.chaptersInstructions || Object.keys(projectData.chaptersInstructions).length === 0) {
        const chaptersInstructions: Record<string, string> = {};
        projectTemplateChapters.forEach((chapter) => {
          chaptersInstructions[chapter.slug] = "";
        });
        updateProjectData({ chaptersInstructions });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData.projectTemplateSlug, projectTemplates]);

  const fileInput = (
    <input
      type="file"
      ref={attachmentFileInputRef}
      accept={ACCEPTED_FILE_TYPES}
      style={{ display: "none" }}
      multiple={true}
      disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading}
      onChange={async (e) => {
        if (e.target.files && e.target.files.length > 0) {
          const newFiles = Array.from(e.target.files);

          const errorMessage = validateFilesLimit({
            files: newFiles,
            maxFiles: MAX_FILES,
            maxTotalSizeBytes: MAX_TOTAL_SIZE_BYTES,
            existingFilesCount: files.length,
            existingTotalSizeBytes: files.reduce((acc, file) => acc + file.size, 0)
          });
          if (errorMessage) {
            toast.error(errorMessage);
            if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = "";
            return;
          }
          toast.dismiss();
          await addFiles(newFiles);
          if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = "";
        }
      }}
    />
  );

  const isFileDeleting = (file: TMinFileOrFile) => {
    if (file instanceof File) return false;
    return file.isDeleting;
  };

  const isFileUploading = (file: TMinFileOrFile) => {
    if (file instanceof File) return false;
    return file.isUploading;
  };

  return (
    <div className="flex flex-col gap-8 w-full pt-8 pb-[80px]">
      <div className={cn("flex flex-col gap-6")}>
        <div className="flex flex-col items-start gap-4">
          <Typography variant="heading5">Edit Project</Typography>
        </div>
      </div>

      <div className="flex flex-col gap-y-4">
        <div className="flex gap-10">
          <div className="flex flex-col flex-grow gap-6">
            <AppInput
              label="Project Name*"
              value={projectData.name}
              onChange={(e) => {
                updateProjectData({ name: e.target.value });
                updateErrors({ name: "" });
              }}
              placeholder="e.g, Acme Project"
              inputError={errors.name}
              disabled={isLoading}
            />
          </div>
        </div>

        <AppTextarea
          label="Short Description"
          value={projectData.description ?? ""}
          fullWidth
          placeholder="e.g, To embed real-time, stage-aware, decision intelligence into every venture..."
          onChange={(e) => {
            updateProjectData({ description: e.target.value });
            updateErrors({ description: "" });
          }}
          textareaError={errors.description}
          isOptional
          className="min-h-[38px]"
          disabled={isLoading}
        />

        <SelectionDropdown
          label="Language Output*"
          options={languageOptions}
          placeholder="Select Language"
          value={projectData.language}
          onChange={(value) => {
            updateProjectData({ language: value });
            updateErrors({ language: "" });
          }}
          error={errors.language}
          disabled={isLoading}
        />
      </div>

      <Typography variant="heading6" className={cn("flex gap-2 items-center")}>
        Document Vault
        <Typography component="span" variant="body3" color="zinc400" className="font-light">
          Optional
        </Typography>
      </Typography>
      <div className="flex flex-col gap-6">
        <Typography variant="body2" color="zinc700" className="max-w-[600px]">
          Upload relevant files containing research, market analysis, business cases, or internal documentation.
          Supported formats: PDF, PowerPoint, Word, Excel, Images, Text, and CSV files. You can also add web links to
          scrape content from web pages.
        </Typography>
        <div className="flex flex-col px-4 py-5 gap-y-4 border border-innoveraZinc-150 rounded-[12px]">
          {files.length > 0 && (
            <div className="flex flex-col items-start gap-3">
              <div className="flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AppButton
                      type="button"
                      disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading}
                      appearance="outlined"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        attachmentFileInputRef.current?.click();
                      }}
                      startAdornment={<UploadSimpleIcon />}
                      color="zinc100"
                    >
                      Upload Files
                    </AppButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Typography variant="body2">
                      Upload up to {MAX_FILES} files (max {MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB total)
                    </Typography>
                  </TooltipContent>
                </Tooltip>
                {onWebLinkAdd && (
                  <AppButton
                    type="button"
                    disabled={isLoading}
                    appearance="outlined"
                    onClick={() => setWebLinkDialogOpen(true)}
                    startAdornment={<GlobeIcon />}
                    color="zinc100"
                  >
                    Add Web Link
                  </AppButton>
                )}
              </div>
            </div>
          )}

          {files.length > 0 ? (
            <div className="flex flex-col gap-3">
              {files.map((file) => (
                <RepositoryFile
                  key={file.name}
                  file={file}
                  onDelete={() => removeFile(file)}
                  disabled={isLoading || isFileDeleting(file)}
                  isDeleting={isFileDeleting(file)}
                  isUploading={isFileUploading(file)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-5 items-center justify-center w-full max-w-[828px] h-[240px] text-center">
              <Typography variant="body3" color="zinc500">
                Upload up to {MAX_FILES} files (max {MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB total)
              </Typography>
              <div className="flex gap-2">
                <AppButton
                  type="button"
                  disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading}
                  appearance="outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    attachmentFileInputRef.current?.click();
                  }}
                  startAdornment={<UploadSimpleIcon />}
                  color="zinc100"
                >
                  Upload Files
                </AppButton>
                {onWebLinkAdd && (
                  <AppButton
                    type="button"
                    disabled={isLoading}
                    appearance="outlined"
                    onClick={() => setWebLinkDialogOpen(true)}
                    startAdornment={<GlobeIcon />}
                    color="zinc100"
                  >
                    Add Web Link
                  </AppButton>
                )}
              </div>
            </div>
          )}
        </div>

        {fileInput}

        <Dialog
          open={webLinkDialogOpen}
          onOpenChange={(open) => {
            if (!isAddingWebLink) {
              setWebLinkDialogOpen(open);
              if (!open) setWebLinkUrl("");
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Web Link</DialogTitle>
            </DialogHeader>
            <AppInput
              label="URL"
              placeholder="https://example.com"
              value={webLinkUrl}
              onChange={(e) => setWebLinkUrl(e.target.value)}
              disabled={isAddingWebLink}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddWebLink();
              }}
            />
            <DialogFooter>
              <AppButton
                appearance="outlined"
                color="zinc150"
                onClick={() => {
                  setWebLinkDialogOpen(false);
                  setWebLinkUrl("");
                }}
                disabled={isAddingWebLink}
              >
                Cancel
              </AppButton>
              <AppButton onClick={handleAddWebLink} isLoading={isAddingWebLink} disabled={!webLinkUrl.trim()}>
                Add
              </AppButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Typography variant="heading6">Decision Context</Typography>

      <div className="flex flex-col gap-y-4">
        <AnalysisDropdown
          value={projectData.projectTemplateSlug}
          onChange={(slug) => {
            updateProjectData({ projectTemplateSlug: slug });
            updateErrors({ projectTemplateSlug: "" });
          }}
          error={errors.projectTemplateSlug}
          projectInstructions={projectData.projectInstructions || ""}
          chaptersInstructions={projectData.chaptersInstructions || {}}
          onInstructionsChange={(projectInstructions, chaptersInstructions) => {
            updateProjectData({ projectInstructions, chaptersInstructions });
          }}
          disabled={true}
        />

        <AppTextarea
          label="Question*"
          value={projectData.decisionContext ?? ""}
          fullWidth
          placeholder="e.g., Whether to productize a new invention"
          onChange={(e) => {
            updateProjectData({ decisionContext: e.target.value });
            updateErrors({ decisionContext: "" });
          }}
          textareaError={errors.decisionContext}
          className="min-h-[120px]"
          disabled={isLoading}
        />
        <Typography variant="body3" color="zinc500">
          Describe the core question or choice this analysis will resolve.
        </Typography>

        <AppTextarea
          label="How do you measure success?"
          value={projectData.successMetrics ?? ""}
          fullWidth
          placeholder="e.g., $50M operating profit by year 4, 25% faster project delivery, 10% increase in market share"
          onChange={(e) => {
            updateProjectData({ successMetrics: e.target.value });
            updateErrors({ successMetrics: "" });
          }}
          textareaError={errors.successMetrics}
          className="min-h-[120px]"
          isOptional
          disabled={isLoading}
        />
        <Typography variant="body3" color="zinc500">
          Describe what success looks like and how you'll measure it. You can include measurable targets (KPIs) or
          qualitative results (e.g., better collaboration, improved brand trust).
        </Typography>

        <AppTextarea
          label="Who will this analysis be presented to?"
          value={projectData.targetAudience ?? ""}
          fullWidth
          placeholder="e.g., Division GM, M&A Committee, CEO"
          onChange={(e) => {
            updateProjectData({ targetAudience: e.target.value });
            updateErrors({ targetAudience: "" });
          }}
          textareaError={errors.targetAudience}
          className="min-h-[38px]"
          isOptional
          disabled={isLoading}
        />
        <Typography variant="body3" color="zinc500">
          This helps tailor the tone, depth, and strategic focus of your report to the right audience level.
        </Typography>

        <AppTextarea
          label="Anything else we should know before starting the analysis?"
          value={projectData.additionalContext ?? ""}
          fullWidth
          onChange={(e) => {
            updateProjectData({ additionalContext: e.target.value });
            updateErrors({ additionalContext: "" });
          }}
          textareaError={errors.additionalContext}
          isOptional
          className="min-h-[120px]"
          disabled={isLoading}
        />

        {isLabs && (
          <>
            <AppTextarea
              label="Presentation Generation Instructions"
              value={projectData.presentationPrompt ?? ""}
              fullWidth
              placeholder={`Customize how presentation slides are generated. Leave empty for default behavior.

Example:
- Create 10-12 slides focused on executive summary
- Include financial projections and market analysis
- Use simple language suitable for board presentation`}
              onChange={(e) => {
                updateProjectData({ presentationPrompt: e.target.value });
              }}
              isOptional
              className="min-h-[120px]"
              disabled={isLoading}
            />
            <Typography variant="body3" color="zinc500">
              Custom instructions for AI when generating presentation slides. Describes slide structure, content focus,
              and formatting preferences. Leave empty to use default settings.
            </Typography>
          </>
        )}
      </div>

      <div className="flex gap-2 ml-auto pb-8">
        <AppButton
          type="button"
          appearance="outlined"
          className="w-fit ml-auto"
          color="zinc150"
          disabled={isLoading}
          onClick={onCancel}
        >
          Cancel
        </AppButton>
        <AppButton
          type="button"
          isLoading={isLoading}
          loadingText="Updating..."
          className="w-fit ml-auto"
          onClick={() => {
            if (validateForm()) {
              onSubmit?.();
            }
          }}
        >
          Update Project
        </AppButton>
      </div>
    </div>
  );
}
